type SentryEventLike = {
  message?: string;
  environment?: string;
  transaction?: string;
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  exception?: { values?: Array<{ type?: string; value?: string }> };
};

function eventText(event: SentryEventLike): string {
  const chunks: string[] = [];
  if (event.message) chunks.push(event.message);
  const first = event.exception?.values?.[0];
  if (first?.type) chunks.push(String(first.type));
  if (first?.value) chunks.push(String(first.value));
  return chunks.join(" ");
}

/** Drop known noise so Sentry Issues stay actionable (REMIX J/H/K/C/F/9, etc.). */
export function sentryBeforeSend<T extends SentryEventLike>(event: T): T | null {
  if (event.environment === "development") return null;
  const text = eventText(event);
  if (text.includes("[remix:hmr]")) return null;
  // Empty / non-actionable client errors (extensions, aborted loads, replay).
  if (/no error message/i.test(text)) return null;
  // AWS Lambda concurrency — not actionable as a frontend exception; monitor in CloudWatch.
  if (text.includes("ConcurrentInvocationLimitExceeded")) return null;
  // Supabase SSR client: concurrent refresh / multi-tab — expected contention.
  if (
    text.includes("was released because another request stole it") ||
    text.includes("Lock was stolen by another request")
  ) {
    return null;
  }
  // Dev: API not running or HMR reconnect.
  if (text.includes("Failed to fetch") && text.includes("localhost")) return null;
  const authEvent = event.tags?.auth_event;
  const authEventStr = typeof authEvent === "string" ? authEvent : "";
  const tx = event.transaction ?? "";
  const pathRaw = event.extra?.path;
  const path = typeof pathRaw === "string" ? pathRaw : "";
  const isLoginSurface = path === "/login" || tx.includes("login");
  if (authEventStr === "auth.recover_failed") {
    if (event.extra?.reason === "no_session" && isLoginSurface) return null;
  }
  if (authEventStr === "auth.visibility_user_missing" && isLoginSurface) {
    return null;
  }
  return event;
}
